import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import API_ENDPOINTS from '../api/endpoints';
import EventSourceWithHeaders from '../utils/EventSourceWithHeaders';

/**
 * SSE hook that subscribes to NicePhone call events for the current user.
 * Uses EventSourceWithHeaders so x-api-key header is sent (no query param needed).
 *
 * @param {(event: object) => void} onCallEvent  called for every `call_event`
 * @param {() => void} [onConnected]             called each time the stream (re)connects —
 *                                               use it to reconcile state missed while offline
 * @param {boolean} [enabled=true]               set false to skip subscribing
 */
const useNicePhoneSSE = (onCallEvent, onConnected, enabled = true) => {
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const callbackRef = useRef(onCallEvent);
  const connectedRef = useRef(onConnected);
  const userRef = useRef(user);
  callbackRef.current = onCallEvent;
  connectedRef.current = onConnected;
  userRef.current = user;

  const userCode = user?.userCode;
  const isAdminUser = !!user?.userIsAdmin;

  useEffect(() => {
    if (!enabled || !isAuthenticated || !userCode || isAdminUser) return undefined;

    const token = userRef.current?.token || localStorage.getItem('token') || localStorage.getItem('x-api-key');
    if (!token) return undefined;

    const es = new EventSourceWithHeaders(API_ENDPOINTS.nicephone.events, {
      headers: { 'x-api-key': token },
      reconnectInterval: 5000,
    });

    es.addEventListener('connected', () => {
      connectedRef.current?.();
    });

    es.addEventListener('call_event', (e) => {
      try {
        const data = JSON.parse(e.data);
        callbackRef.current?.(data);
      } catch {}
    });

    return () => {
      es.close();
    };
    // Re-subscribe only when the identity changes, not on every user object refresh.
  }, [enabled, isAuthenticated, userCode, isAdminUser]);
};

export default useNicePhoneSSE;
